import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const TIMEOUT_MS = 60 * 60 * 1000; // 15 minutes

const IdleTimeout = () => {
    const { isAuthenticated, logout } = useAuthStore();
    const navigate = useNavigate();

    const handleLogout = useCallback(async () => {
        if (isAuthenticated) {
            toast.error('Session expired due to inactivity');
            localStorage.removeItem('lastActivity');
            await logout();
            navigate('/auth/login');
        }
    }, [isAuthenticated, logout, navigate]);

    // Check periodically if we exceeded the timeout since last activity
    useEffect(() => {
        if (!isAuthenticated) return;

        const updateActivity = () => {
            localStorage.setItem('lastActivity', Date.now().toString());
        };

        // Initialize or check last activity on mount
        const storedActivity = localStorage.getItem('lastActivity');
        if (storedActivity) {
            const timeSinceLastActivity = Date.now() - parseInt(storedActivity, 10);
            if (timeSinceLastActivity >= TIMEOUT_MS) {
                handleLogout();
                return; // Expired while away, stop setup
            }
        } else {
            updateActivity();
        }

        // Check every 10 seconds if we've been idle for TIMEOUT_MS
        const checkInterval = setInterval(() => {
            const now = Date.now();
            const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0', 10);

            if (now - lastActivity >= TIMEOUT_MS) {
                handleLogout();
            }
        }, 10000);

        // Update the last activity timestamp on events
        const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
        events.forEach(event => {
            window.addEventListener(event, updateActivity, { passive: true });
        });

        // Listen for storage changes from other tabs
        const handleStorageChange = (e) => {
            if (e.key === 'lastActivity') {
                // Storage event fires when ANOTHER tab updates the value
                // We don't need to do anything explicit here since the interval 
                // will read the fresh value automatically, but this listener 
                // ensures we're reacting to other tabs if necessary.
            }
        };
        window.addEventListener('storage', handleStorageChange);

        return () => {
            clearInterval(checkInterval);
            events.forEach(event => {
                window.removeEventListener(event, updateActivity, { passive: true });
            });
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [isAuthenticated, handleLogout]);

    return null; // Component does not render any UI
};

export default IdleTimeout;
